import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_NAME } from "@/config";
import { createPasskey, passkeysSupported, deviceName, passkeyError, answerWithPasskeyPrf, passkeyPrf, NO_PRF } from "@/lib/webauthn";
import { newKeyPair, newRecoveryCode, parseRecoveryCode, sealForPasskey, sealForRecovery, openWithPasskey,
  openWithRecovery } from "@/lib/vault";
import { PageHeader, Card, Loading, StatusBadge } from "@/components/common";
import { PROVIDER_NAMES, startSocial } from "@/lib/social";
import { ProviderLogo } from "@/components/ProviderLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, LockKeyhole, Plus, Trash2, Loader2, Copy, Download } from "lucide-react";
import { DeleteAccountCard } from "@/components/AccountDeletion";
import { GraphiteScope } from "@/components/Graphite";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "never");
const errorOf = (e) => passkeyError(e) || apiErr(e);

function RemoveDialog({ passkey, lastOpener, onClose }) {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const remove = useMutation({
    mutationFn: async () => (await api.post(`/passkeys/${passkey.id}/remove`, { password })).data,
    onSuccess: () => { toast.success("Passkey removed"); qc.invalidateQueries({ queryKey: ["passkeys"] }); qc.invalidateQueries({ queryKey: ["vault"] }); refresh(); onClose(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  return (
    <Dialog open={!!passkey} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Remove "{passkey?.name}"</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); remove.mutate(); }} className="space-y-3">
          {lastOpener && (
            <p className="text-sm text-gr-warning" data-testid="passkey-remove-last-opener">
              This is the only passkey that opens your protected sessions. Without it they open again only once you set
              up another passkey with your recovery code.
            </p>
          )}
          <p className="text-sm text-muted-foreground">Confirm with your password. You'll get an email about it.</p>
          <Input type="password" autoComplete="current-password" value={password} placeholder="Your password"
            onChange={(e) => setPassword(e.target.value)} data-testid="passkey-remove-password" />
          <DialogFooter>
            <Button type="submit" variant="destructive" disabled={!password || remove.isPending} data-testid="passkey-remove-confirm">
              {remove.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Remove passkey
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// The recovery code, shown once: it's made in this page and never sent, so nobody can show it again.
function RecoveryCodeDialog({ code, onClose }) {
  const [saved, setSaved] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(code); toast.success("Recovery code copied"); } catch { toast.error("Couldn't copy: select the code instead."); } };
  const download = () => {
    const text = `${APP_NAME} recovery code\n\n${code}\n\nIt opens your protected sessions if you lose your passkeys. Keep it somewhere safe.\n`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    a.download = `${APP_NAME.toLowerCase()}-recovery-code.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const done = () => { setSaved(false); onClose(); };
  return (
    <Dialog open={!!code} onOpenChange={(open) => !open && saved && done()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Save your recovery code</DialogTitle></DialogHeader>
        <p className="text-sm text-gr-ink-secondary leading-relaxed">
          If you lose every passkey that opens your sessions, this code is the only way back in. Nobody at {APP_NAME} or
          your company can recover it, and it won't be shown again.
        </p>
        <div className="font-mono text-base tracking-wider text-center py-4 rounded-lg border bg-background select-all break-all" data-testid="recovery-code">{code}</div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={copy}><Copy className="w-4 h-4" /> Copy</Button>
          <Button type="button" variant="outline" onClick={download}><Download className="w-4 h-4" /> Download</Button>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={saved} onCheckedChange={(v) => setSaved(!!v)} data-testid="recovery-code-saved" />
          I saved my recovery code somewhere safe
        </label>
        <DialogFooter>
          <Button type="button" disabled={!saved} onClick={done} data-testid="recovery-code-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Set up a passkey to open the sessions: open the private key with a passkey that already can (or the recovery
// code), then seal it under this passkey's PRF output.
function SetupDialog({ passkey, vault, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const openers = Object.keys(vault?.slots || {});
  const [password, setPassword] = useState("");
  const [withCode, setWithCode] = useState(false);
  const [code, setCode] = useState("");
  const useCode = withCode || openers.length === 0;
  const close = () => { setPassword(""); setCode(""); setWithCode(false); onClose(); };
  const setup = useMutation({
    mutationFn: async () => {
      const { data: got } = await api.post("/vault/options", { password, purpose: "slot", passkey_id: passkey.id });
      let privateKey;
      if (useCode) {
        const bytes = parseRecoveryCode(code);
        if (!bytes) throw new Error("That isn't a recovery code: it has 32 letters and digits.");
        try { privateKey = await openWithRecovery(vault.recovery, bytes, user.id); }
        catch { throw new Error("That recovery code doesn't open your sessions."); }
      } else {
        const opened = await passkeyPrf(openers, vault.prf_salt);
        if (!opened.prf) throw new Error(NO_PRF);
        privateKey = await openWithPasskey(vault.slots[opened.credentialId], opened.prf, user.id, opened.credentialId);
      }
      const { credential, prf } = await answerWithPasskeyPrf(got.options);
      if (!prf) throw new Error(NO_PRF);
      const slot = await sealForPasskey(privateKey, prf, user.id, credential.id);
      return (await api.post("/vault/slots", { challenge_id: got.challenge_id, credential, slot })).data;
    },
    onSuccess: () => {
      toast.success(`"${passkey.name}" now opens your sessions`);
      qc.invalidateQueries({ queryKey: ["passkeys"] }); qc.invalidateQueries({ queryKey: ["vault"] }); close();
    },
    onError: (e) => toast.error(errorOf(e)),
  });
  return (
    <Dialog open={!!passkey} onOpenChange={(open) => !open && close()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Set up "{passkey?.name}"</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); setup.mutate(); }} className="space-y-3">
          <p className="text-sm text-gr-ink-secondary leading-relaxed">
            {useCode ? "Your recovery code opens your sessions' key here, in this browser, then this passkey seals it for itself."
              : "A passkey that already opens your sessions opens their key here, in this browser, then this passkey seals it for itself. Your browser asks twice."}
          </p>
          {useCode && (
            <div className="space-y-1.5">
              <Label htmlFor="setup-code">Recovery code</Label>
              <Input id="setup-code" autoComplete="off" spellCheck={false} className="font-mono" value={code}
                placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX" onChange={(e) => setCode(e.target.value)} data-testid="setup-code" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="setup-password">Your password</Label>
            <Input id="setup-password" type="password" autoComplete="current-password" value={password}
              onChange={(e) => setPassword(e.target.value)} data-testid="setup-password" />
          </div>
          {openers.length > 0 && (
            <button type="button" className="text-sm underline underline-offset-2 text-gr-ink-secondary" onClick={() => setWithCode(!withCode)}>
              {withCode ? "Use a passkey that opens your sessions instead" : "Use your recovery code instead"}
            </button>
          )}
          <DialogFooter>
            <Button type="submit" disabled={!password || (useCode && !code) || setup.isPending} data-testid="setup-confirm">
              {setup.isPending && <Loader2 className="w-4 h-4 animate-spin" />} Set up
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Passkeys({ vault }) {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const [name, setName] = useState(deviceName);
  const [password, setPassword] = useState("");
  const [removing, setRemoving] = useState(null);
  const [settingUp, setSettingUp] = useState(null);
  const { data, isLoading } = useQuery({ queryKey: ["passkeys"], queryFn: async () => (await api.get("/passkeys")).data });
  const add = useMutation({
    mutationFn: async () => {
      // a passkey unlocks sessions: adding one takes the password, not just being signed in
      const { data: got } = await api.post("/passkeys/register/options", { password });
      const credential = await createPasskey(got.options);
      return (await api.post("/passkeys/register", { challenge_id: got.challenge_id, credential, name })).data;
    },
    onSuccess: () => { toast.success("Passkey added"); setPassword(""); qc.invalidateQueries({ queryKey: ["passkeys"] }); refresh(); },
    onError: (e) => toast.error(errorOf(e)),
  });

  if (isLoading) return <Loading />;
  const list = data?.passkeys || [];
  const protectedOn = !!vault?.enabled;
  const openers = list.filter((p) => p.opens_sessions).length;
  return (
    <Card data-testid="passkeys-card">
      <h2 className="font-display text-h4 font-semibold flex items-center gap-2"><KeyRound className="w-5 h-5" /> Passkeys</h2>
      <p className="text-sm text-gr-ink-secondary mt-2 mb-6 leading-relaxed">
        Sign in and unlock your sessions with your fingerprint, face or device PIN. Once you have a passkey, your
        sessions unlock only with it: a password manager can't unlock them for you. Lost the device? Sign in with
        your password ("Forgot password" if needed) and remove it here, or ask your company's founder.
      </p>
      {!data?.enabled ? <p className="text-sm text-muted-foreground">Passkeys aren't switched on here yet.</p> : (
        <>
          {list.length > 0 && (
            <div className="divide-y border rounded-lg bg-background mb-6">
              {list.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid="passkey-row">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{p.name}</div>
                    <div className="text-sm text-muted-foreground">Added {when(p.created_at)} · last used {when(p.last_used_at)}
                      {p.backed_up ? " · synced" : ""}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {protectedOn && (p.opens_sessions
                      ? <StatusBadge status="active" label="Opens your sessions" tone="info" />
                      : <Button size="sm" variant="outline" onClick={() => setSettingUp(p)} data-testid="passkey-setup">Set up</Button>)}
                    <Button size="icon" variant="ghost" aria-label={`Remove ${p.name}`} title="Remove" onClick={() => setRemoving(p)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {!passkeysSupported() ? <p className="text-sm text-muted-foreground">This browser can't use passkeys.</p> : (
            <form className="flex flex-wrap gap-2 items-end" onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
              <div className="flex-1 min-w-40 space-y-1.5">
                <Label htmlFor="passkey-name">Name</Label>
                <Input id="passkey-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-testid="passkey-name" />
              </div>
              <div className="flex-1 min-w-40 space-y-1.5">
                <Label htmlFor="passkey-password">Your password</Label>
                <Input id="passkey-password" type="password" autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} data-testid="passkey-password" />
              </div>
              {/* as tall as the fields beside it */}
              <Button type="submit"  disabled={!password || add.isPending} data-testid="passkey-add">
                {add.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add a passkey
              </Button>
            </form>
          )}
        </>
      )}
      <RemoveDialog passkey={removing} lastOpener={protectedOn && removing?.opens_sessions && openers === 1}
        onClose={() => setRemoving(null)} />
      <SetupDialog passkey={settingUp} vault={vault} onClose={() => setSettingUp(null)} />
    </Card>
  );
}

// Passkey protection for session keys (backend/vault.py): independent professionals only, opt-in, for good.
function SessionProtection({ vault, passkeyCount }) {
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [code, setCode] = useState(null);
  const [renewing, setRenewing] = useState(false);
  const finish = (newCode) => {
    setPassword(""); setRenewing(false); setCode(newCode);
    qc.invalidateQueries({ queryKey: ["vault"] }); qc.invalidateQueries({ queryKey: ["passkeys"] }); refresh();
  };
  const enable = useMutation({
    mutationFn: async () => {
      const { data: got } = await api.post("/vault/options", { password, purpose: "enable" });
      const { credential, prf } = await answerWithPasskeyPrf(got.options);
      if (!prf) throw new Error(NO_PRF);
      const pair = await newKeyPair();
      const recovery = newRecoveryCode();
      await api.post("/vault/enable", {
        challenge_id: got.challenge_id, credential, public_key: pair.publicKey,
        slot: await sealForPasskey(pair.privateKey, prf, user.id, credential.id),
        recovery: await sealForRecovery(pair.privateKey, recovery.bytes, user.id),
      });
      return recovery.code;
    },
    onSuccess: (newCode) => { toast.success("Your sessions are now protected by your passkey"); finish(newCode); },
    onError: (e) => toast.error(errorOf(e)),
  });
  const renew = useMutation({
    mutationFn: async () => {
      const { data: got } = await api.post("/vault/options", { password, purpose: "recovery" });
      const { credential, prf } = await answerWithPasskeyPrf(got.options);
      if (!prf) throw new Error(NO_PRF);
      const privateKey = await openWithPasskey(vault.slots[credential.id], prf, user.id, credential.id);
      const recovery = newRecoveryCode();
      await api.post("/vault/recovery", { challenge_id: got.challenge_id, credential,
        recovery: await sealForRecovery(privateKey, recovery.bytes, user.id) });
      return recovery.code;
    },
    onSuccess: (newCode) => { toast.success("New recovery code made: the old one no longer works"); finish(newCode); },
    onError: (e) => toast.error(errorOf(e)),
  });
  const openers = Object.keys(vault.slots || {}).length;
  const passwordForm = (mutation, label, testid) => (
    <form className="flex flex-wrap gap-2 items-end" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
      <div className="flex-1 min-w-40 space-y-1.5">
        <Label htmlFor={`${testid}-password`}>Your password</Label>
        <Input id={`${testid}-password`} type="password" autoComplete="current-password" value={password}
          onChange={(e) => setPassword(e.target.value)} data-testid={`${testid}-password`} />
      </div>
      <Button type="submit" disabled={!password || mutation.isPending} data-testid={testid}>
        {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />} {label}
      </Button>
    </form>
  );
  return (
    <Card data-testid="session-protection-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-h4 font-semibold flex items-center gap-2"><LockKeyhole className="w-5 h-5" /> Session protection</h2>
        <StatusBadge status={vault.enabled ? "active" : "off"} label={vault.enabled ? "On" : "Off"} tone={vault.enabled ? "info" : "muted"} />
      </div>
      {vault.enabled ? (
        <>
          <p className="text-sm text-gr-ink-secondary mt-2 mb-4 leading-relaxed">
            On since {when(vault.created_at)}. Your locked sessions open only with a passkey marked "Opens your sessions"
            above, or with your recovery code. Not your password, and nobody at {APP_NAME} or your company can open them.
            While a session is unlocked, {APP_NAME} still handles its content to run it.
          </p>
          {openers === 0 && (
            <p className="text-sm text-gr-warning mb-4">None of your passkeys opens your sessions: add one above if needed,
              then press its Set up and use your recovery code.</p>
          )}
          {renewing ? passwordForm(renew, "Make a new recovery code", "vault-renew") : (
            <Button variant="outline" disabled={openers === 0} onClick={() => setRenewing(true)} data-testid="vault-renew-open">
              New recovery code
            </Button>
          )}
        </>
      ) : (
        <>
          <p className="text-sm text-gr-ink-secondary mt-2 mb-4 leading-relaxed">
            Tie your sessions' keys to your passkey. Locked sessions then open only with it or a recovery code you
            keep, never with your password, and nobody at {APP_NAME} or your company can open them. If you lose every
            passkey and the recovery code, their history is gone for good (your code stays in the repository). This
            can't be turned off.
          </p>
          {passkeyCount === 0 ? <p className="text-sm text-muted-foreground">Add a passkey above first.</p>
            : passwordForm(enable, "Turn on", "vault-enable")}
        </>
      )}
      <RecoveryCodeDialog code={code} onClose={() => setCode(null)} />
    </Card>
  );
}

// Google and GitHub as ways to sign in (backend/social.py): linking one takes the password, like adding a passkey
function PasswordDialog({ open, title, children, action, destructive, pending, onConfirm, onClose }) {
  const [password, setPassword] = useState("");
  const close = () => { setPassword(""); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onConfirm(password); }} className="space-y-3">
          <p className="text-sm text-muted-foreground">{children}</p>
          <Input type="password" autoComplete="current-password" value={password} placeholder="Your password"
            onChange={(e) => setPassword(e.target.value)} data-testid="social-password" />
          <DialogFooter>
            <Button type="submit" variant={destructive ? "destructive" : "default"} disabled={!password || pending} data-testid="social-confirm">
              {pending && <Loader2 className="w-4 h-4 animate-spin" />} {action}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SocialSignIn() {
  const qc = useQueryClient();
  const [linking, setLinking] = useState(null);
  const [removing, setRemoving] = useState(null);
  const { data } = useQuery({ queryKey: ["identities"], queryFn: async () => (await api.get("/auth/social/identities")).data });
  const link = useMutation({
    mutationFn: (password) => startSocial(linking, "link", password),  // the browser goes to the provider
    onError: (e) => toast.error(apiErr(e)),
  });
  const unlink = useMutation({
    mutationFn: async (password) => (await api.post(`/auth/social/${removing}/unlink`, { password })).data,
    onSuccess: () => { toast.success(`${PROVIDER_NAMES[removing]} removed`); qc.invalidateQueries({ queryKey: ["identities"] }); setRemoving(null); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data?.available?.length) return null;
  const linked = Object.fromEntries((data.identities || []).map((i) => [i.provider, i]));
  return (
    <Card data-testid="social-card">
      <h2 className="font-display text-h4 font-semibold">Google and GitHub</h2>
      <p className="text-sm text-gr-ink-secondary mt-2 mb-6 leading-relaxed">
        Sign in with your Google or GitHub account instead of typing your password. Unlocking sessions still takes your
        password or passkey.
      </p>
      <div className="divide-y border rounded-lg bg-background">
        {data.available.map((provider) => (
          <div key={provider} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`social-row-${provider}`}>
            <div className="min-w-0 flex items-center gap-3">
              <ProviderLogo provider={provider} className="w-5 h-5" />
              <div className="min-w-0">
                <div className="font-semibold">{PROVIDER_NAMES[provider]}</div>
                <div className="text-sm text-muted-foreground truncate">{linked[provider]
                  ? `Signs you in${linked[provider].email ? ` · ${linked[provider].email}` : ""} · linked ${when(linked[provider].linked_at)}`
                  : "Not linked"}</div>
              </div>
            </div>
            {linked[provider]
              ? <Button size="sm" variant="ghost" onClick={() => setRemoving(provider)} data-testid={`social-remove-${provider}`}>Remove</Button>
              : <Button size="sm" variant="outline" onClick={() => setLinking(provider)} data-testid={`social-link-${provider}`}>Link</Button>}
          </div>
        ))}
      </div>
      <PasswordDialog open={!!linking} title={`Link ${PROVIDER_NAMES[linking] || ""}`} action="Continue" pending={link.isPending}
        onConfirm={(pw) => link.mutate(pw)} onClose={() => setLinking(null)}>
        Confirm with your password, then sign in to {PROVIDER_NAMES[linking]}. You'll get an email about it.
      </PasswordDialog>
      <PasswordDialog open={!!removing} title={`Remove ${PROVIDER_NAMES[removing] || ""}`} action="Remove" destructive
        pending={unlink.isPending} onConfirm={(pw) => unlink.mutate(pw)} onClose={() => setRemoving(null)}>
        You'll sign in with your password or passkey instead. Confirm with your password.
      </PasswordDialog>
    </Card>
  );
}

export default function Account() {
  const { user } = useAuth();
  const coder = user?.role === "coder";
  const { data: vault } = useQuery({ queryKey: ["vault"], enabled: coder, queryFn: async () => (await api.get("/vault")).data });
  const { data: mine } = useQuery({ queryKey: ["passkeys"], queryFn: async () => (await api.get("/passkeys")).data });
  return (
    <GraphiteScope data-testid="account-page">
      <PageHeader title="Account" subtitle={`${user?.name || ""} · ${user?.email || ""}`} />
      <div className="max-w-2xl space-y-6">
        <Passkeys vault={vault} />
        {coder && vault?.available && passkeysSupported() && (
          <SessionProtection vault={vault} passkeyCount={(mine?.passkeys || []).length} />
        )}
        {!["platform_admin", "viewer"].includes(user?.role) && <SocialSignIn />}
        {/* owners look for it here first (it's also at the bottom of Settings) */}
        {user?.role === "owner" && <DeleteAccountCard />}
      </div>
    </GraphiteScope>
  );
}
